import { useContext, ReactNode, useEffect, memo } from "react";
import { Box, Grid } from "@mui/material";
import { ACTION_TYPE, JobBuilderContext } from "context/jobBuilderContext";
import AddressAutocomplete from "components/autocompletes/AddressAutocomplete";
import { AddressSuggestion, Location } from "types";
import AuturaTextField from "components/inputs/AuturaTextField";
import { useTranslation } from "react-i18next";
import AuturaStateField from "components/inputs/AuturaStateField";

const LocationStep = (): ReactNode => {
  const { t } = useTranslation();
  const { state: jobState, dispatch } = useContext(JobBuilderContext);

  useEffect(() => {
    window.scroll({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const setStreet = (street: string) => {
    dispatch({
      type: ACTION_TYPE.SET_CHANGED_DEFAULT_ADDRESS,
      payload: true,
    });
    dispatch({
      type: ACTION_TYPE.SET_STREET,
      payload: street,
    });
  };
  const setLandmark = (landmark: string) => {
    dispatch({
      type: ACTION_TYPE.SET_LANDMARK,
      payload: landmark,
    });
  };
  const setCity = (city: string) => {
    dispatch({
      type: ACTION_TYPE.SET_CHANGED_DEFAULT_ADDRESS,
      payload: true,
    });
    dispatch({
      type: ACTION_TYPE.SET_CITY,
      payload: city,
    });
  };
  const setStateCode = (stateCode: string) => {
    dispatch({
      type: ACTION_TYPE.SET_CHANGED_DEFAULT_ADDRESS,
      payload: true,
    });
    dispatch({
      type: ACTION_TYPE.SET_STATE_CODE,
      payload: stateCode,
    });
  };
  const setPostalCode = (postalCode: string) => {
    dispatch({
      type: ACTION_TYPE.SET_CHANGED_DEFAULT_ADDRESS,
      payload: true,
    });
    dispatch({
      type: ACTION_TYPE.SET_POSTAL_CODE,
      payload: postalCode,
    });
  };
  const setLocation = (location: Location) => {
    dispatch({
      type: ACTION_TYPE.SET_CHANGED_DEFAULT_ADDRESS,
      payload: true,
    });
    dispatch({
      type: ACTION_TYPE.AUTOCOMPLETE_ADDRESS,
      payload: location,
    });
  };

  let propertyAddressOption: AddressSuggestion | undefined;
  if (jobState.property?.location) {
    propertyAddressOption = {
      text: `${jobState.property?.name}: ${jobState.property?.location?.street}, ${jobState.property?.location?.city}, ${jobState.property?.location?.stateCode}, ${jobState.property?.location?.postalCode}, ${jobState.property?.location?.country}`,
      magicKey: "default_option",
      isCollection: false,
    };
  }

  return (
    <Grid container width={"100%"} spacing={1.5}>
      <Grid mb={3} size={12}>
        <Box sx={{maxWidth: '50%', pr: '6px'}}>
        <AddressAutocomplete
          defaultOption={propertyAddressOption}
          setLocation={setLocation}
          variant={"outlined"}
          label={t("labels.address_lookup", { ns: "jobs" })}
        />
        </Box>
      </Grid>
      <Grid
        size={{
          xs: 12,
          lg: 3,
        }}
      >
        <AuturaTextField
          label={t("job.location.street", { ns: "entities" })}
          name="location.street"
          value={jobState.street || ""}
          onChange={(e) => setStreet(e.target.value)}
          required
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          lg: 3,
        }}
      >
        <AuturaTextField
          label={t("job.location.city", { ns: "entities" })}
          name="location.city"
          value={jobState.city || ""}
          onChange={(e) => setCity(e.target.value)}
          required
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          lg: 3,
        }}
      >
        <AuturaStateField
          label={t("job.location.stateCode", { ns: "entities" })}
          stateCode={jobState.stateCode || ""}
          setStateCode={setStateCode}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          lg: 3,
        }}
      >
        <AuturaTextField
          label={t("job.location.postalCode", { ns: "entities" })}
          name="location.postalCode"
          value={jobState.postalCode || ""}
          onChange={(e) => setPostalCode(e.target.value)}
          required
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          lg: 6,
        }}
      >
        <AuturaTextField
          label={t("labels.location_notes", { ns: "jobs" })}
          name="location.landmark"
          value={jobState.landmark || ""}
          onChange={(e) => setLandmark(e.target.value)}
        />
      </Grid>
    </Grid>
  );
};

export default memo(LocationStep);
